type BackendEnvType = 'docker' | 'staging' | 'production'
type VercelEnv = 'development' | 'preview' | 'production'

const VERCEL_ENV = (process.env.NEXT_PUBLIC_VERCEL_ENV as VercelEnv) || 'development'

const NEXT_PUBLIC_BACKEND_ENV = (() => {
  // For non-development environments, always use 'production'
  if (VERCEL_ENV !== 'development') {
    return 'production' as BackendEnvType
  }

  // For development, check if the env var is set
  const envValue = process.env.NEXT_PUBLIC_BACKEND_ENV as BackendEnvType

  if (!envValue) {
    throw new Error('NEXT_PUBLIC_BACKEND_ENV is not set for development environment')
  }

  return envValue
})()

const getEnvValue = (values: Record<BackendEnvType, string | undefined>) => {
  const output = values[NEXT_PUBLIC_BACKEND_ENV]

  if (!output && VERCEL_ENV !== 'production') {
    console.warn(
      `No value found for ${NEXT_PUBLIC_BACKEND_ENV}. Please check your environment variables.`
    )
  }

  return output
}

// For Auth0
const BASE_URL = (() => {
  switch (VERCEL_ENV) {
    case 'development':
      return `http://${process.env.NEXT_PUBLIC_VERCEL_URL}`
    case 'preview':
      return `https://${process.env.NEXT_PUBLIC_VERCEL_URL}`
    case 'production':
      return `https://${process.env.NEXT_PUBLIC_VERCEL_PROJECT_PRODUCTION_URL}`
    default:
      throw new Error(`Unexpected VERCEL_ENV: ${VERCEL_ENV}`)
  }
})()

const ENV_VARS = {
  BASE_URL,
  VERCEL_ENV,
  IS_PRODUCTION: VERCEL_ENV === 'production' && NEXT_PUBLIC_BACKEND_ENV === 'production',
  AUTH_DOMAIN: getEnvValue({
    docker: process.env.NEXT_PUBLIC_AUTH_DOMAIN_DOCKER,
    staging: process.env.NEXT_PUBLIC_AUTH_DOMAIN_STAGING,
    production: process.env.NEXT_PUBLIC_AUTH_DOMAIN,
  }),
  AUTH_CLIENT_ID: getEnvValue({
    docker: process.env.NEXT_PUBLIC_AUTH_CLIENT_ID_DOCKER,
    staging: process.env.NEXT_PUBLIC_AUTH_CLIENT_ID_STAGING,
    production: process.env.NEXT_PUBLIC_AUTH_CLIENT_ID,
  }),
  CODEX_API_BASE_URL: getEnvValue({
    docker: process.env.NEXT_PUBLIC_CODEX_API_BASE_URL_DOCKER,
    staging: process.env.NEXT_PUBLIC_CODEX_API_BASE_URL_STAGING,
    production: process.env.NEXT_PUBLIC_CODEX_API_BASE_URL,
  }),
  AUTH_API_AUDIENCE: getEnvValue({
    docker: process.env.NEXT_PUBLIC_AUTH_API_AUDIENCE_DOCKER,
    staging: process.env.NEXT_PUBLIC_AUTH_API_AUDIENCE_STAGING,
    production: process.env.NEXT_PUBLIC_AUTH_API_AUDIENCE,
  }),
  COOKIE_DOMAIN: (() => {
    // https://accounts.staging-bc26qf4m.cleanlab.ai -> .staging-bc26qf4m.cleanlab.ai
    // https://accounts.cleanlab.ai -> .cleanlab.ai
    try {
      const hostname = new URL(BASE_URL || '').hostname
      return hostname === 'localhost' ? hostname : '.' + hostname.split('.').slice(1).join('.')
    } catch {
      return 'localhost'
    }
  })(),
  DATADOG_APPLICATION_ID: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
  DATADOG_CLIENT_TOKEN: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
  POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
  POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
  TARGET_PROJECT_ID: process.env.NEXT_PUBLIC_TARGET_PROJECT_ID,
  PINNED_PROJECT_ID: process.env.NEXT_PUBLIC_PINNED_PROJECT_ID,
  VANCOUVER_ORG_ID: process.env.NEXT_PUBLIC_VANCOUVER_ORG_ID,
  STRIPE_PUBLISHABLE_KEY: getEnvValue({
    docker: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY_PREPROD,
    staging: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY_PREPROD,
    production: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
  }),
}

export default ENV_VARS
